import { NovuProvider } from "@novu/notification-center";
import * as Sentry from "@sentry/react";
import { useGetOrganization } from "lib/api/organization/useGetOrganization";
import { castArray } from "lodash";
import posthog from "posthog-js";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import type { OrganizationMembershipDto } from "types/OrganizationMembership";
import {
	getNovuApplicationId,
	useGetNovuHMAC,
} from "utils/getNovuApplicationId";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import {
	OrganizationContext,
	type OrganizationContextType,
} from "./useOrganization";

export interface OrganizationViewProps {
	children: React.ReactNode;
}

const getRolesFromMembership = (
	membership: OrganizationMembershipDto | null | undefined,
) => {
	const role = membership?.role ?? "unknown";
	const roles = castArray(role);
	return roles;
};

export const OrganizationView2: React.FC<OrganizationViewProps> = ({
	children,
}) => {
	const [showLoading, setShowLoading] = useState(false);

	const { organizationId } = useParams<{ organizationId: string }>();
	const { organization } = useGetOrganization(organizationId, false, false);
	useEffect(() => {
		if (!showLoading) {
			return;
		}
		const timeoutHandle = setTimeout(() => setShowLoading(true), 700);
		return () => clearTimeout(timeoutHandle);
	}, [showLoading]);
	const novuHMAC = useGetNovuHMAC(organizationId);
	const novuApplicationId = React.useMemo(() => {
		return getNovuApplicationId();
	}, []);
	useEffect(() => {
		if (organizationId) {
			Sentry.setTag("organizationId", organizationId);
		}
		return () => Sentry.setTag("organizationId", undefined);
	}, [organizationId]);

	useEffect(() => {
		const disableAnalytics = organization?.disableAnalytics ?? false;
		if (disableAnalytics) {
			posthog.opt_out_capturing();
			console.log("disable analytics:", disableAnalytics);
		}
	}, [organization?.disableAnalytics]);

	const context = React.useMemo(() => {
		const roles = getRolesFromMembership(organization?.membership);
		const isAdmin = roles.includes("admin");
		return {
			isValidOrganizationContext: true,
			organization,
			membership: organization?.membership,
			defaultLanguage: organization?.defaultLanguage ?? "en",
			isOrganizationAdmin: isAdmin,
			uiPermissions: organization?.permissions,
			aiFeaturesEnabled: (organization?.disableAI ?? false) === false,
			canAccessFollowUps:
				organization?.followUpsOnlyForAdmins === true ? isAdmin : true,
		} as OrganizationContextType;
	}, [organization]);
	if (!organization?.membership || !context.isValidOrganizationContext) {
		return null;
	}
	return (
		<OrganizationContext.Provider value={context}>
			<SidebarProvider>
				<NovuProvider
					applicationIdentifier={novuApplicationId}
					backendUrl="https://novu-api.kultifyapp.com"
					socketUrl="https://novu-ws.kultifyapp.com"
					subscriberId={organization?.membership._id ?? "UNKNOWN"}
					subscriberHash={novuHMAC ?? ""}
					initialFetchingStrategy={{
						fetchNotifications: false,
						fetchOrganization: true,
						fetchUnseenCount: true,
						fetchUserPreferences: true,
					}}
				>
					<AppSidebar />
					<SidebarInset className="overflow-x-auto">{children}</SidebarInset>
				</NovuProvider>
			</SidebarProvider>
		</OrganizationContext.Provider>
	);
};
